import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { analyticsApi } from '../../services/api';
import { 
  Settings, 
  Save, 
  Trash2, 
  AlertTriangle,
  Globe,
  Building,
  Tag,
  Loader,
  Target,
  Plus,
  X,
  RefreshCw
} from 'lucide-react';

interface ProjectSettingsProps {
  project: any;
  onUpdate: (data: any) => Promise<void>;
  onDelete: () => Promise<void>;
}

export function ProjectSettings({ project, onUpdate, onDelete }: ProjectSettingsProps) {
  const [formData, setFormData] = useState({
    name: project.name || '',
    description: project.description || '',
    websiteUrl: project.websiteUrl || '',
    industry: project.industry || '',
    isActive: project.isActive !== false, // Default to true if undefined
    goals: Array.isArray(project.goals) ? project.goals.map((goal: any, index: number) => ({
      id: goal.id || `existing-${index}`,
      name: goal.name || goal,
      type: goal.type || 'custom',
      value: goal.value || '',
      description: goal.description || ''
    })) : []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [goalOptions, setGoalOptions] = useState<any>(null);
  const [isLoadingGoalOptions, setIsLoadingGoalOptions] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);

  // Update form data when project changes
  useEffect(() => {
    console.log('🔍 ProjectSettings - Project data received:', {
      projectId: project.id,
      projectName: project.name,
      goals: project.goals,
      goalsType: typeof project.goals,
      goalsIsArray: Array.isArray(project.goals),
      goalsLength: Array.isArray(project.goals) ? project.goals.length : 'N/A'
    });
    
    const processedGoals = Array.isArray(project.goals) ? project.goals.map((goal: any, index: number) => {
      // Handle both object and JSON string formats
      let parsedGoal = goal;
      
      if (typeof goal === 'string') {
        try {
          // Try to parse as JSON first
          parsedGoal = JSON.parse(goal);
          console.log('✅ Parsed JSON goal:', parsedGoal);
        } catch (e) {
          // If not JSON, treat as simple string goal name
          parsedGoal = {
            id: `existing-${index}`,
            name: goal,
            type: 'pageview',
            value: '',
            description: ''
          };
          console.log('✅ Created goal from string:', parsedGoal);
        }
      }
      
      // Ensure all required fields exist
      return {
        id: parsedGoal.id || `existing-${index}`,
        name: parsedGoal.name || '',
        type: parsedGoal.type || 'pageview',
        value: parsedGoal.value || '',
        description: parsedGoal.description || ''
      };
    }) : [];
    
    setFormData({
      name: project.name || '',
      description: project.description || '',
      websiteUrl: project.websiteUrl || '',
      industry: project.industry || '',
      isActive: project.isActive !== false,
      goals: processedGoals
    });
  }, [project]);

  // Fetch goal options when component mounts
  useEffect(() => {
    fetchGoalOptions();
  }, [project.id]);

  const fetchGoalOptions = async () => {
    setIsLoadingGoalOptions(true);
    try {
      const options = await analyticsApi.getGoalOptions(project.id);
      setGoalOptions(options);
    } catch (error) {
      console.error('Failed to fetch goal options:', error);
    } finally {
      setIsLoadingGoalOptions(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddGoal = () => {
    // Limit to 5 goals maximum
    if (formData.goals.length >= 5) {
      alert('Maximum of 5 goals allowed per project');
      return;
    }

    const newGoal = {
      id: Date.now().toString(),
      name: '',
      type: 'pageview',
      value: '',
      description: ''
    };
    setFormData(prev => ({
      ...prev,
      goals: [...prev.goals, newGoal]
    }));
  };

  const handleUpdateGoal = (goalId: string, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.map((goal: any) => 
        goal.id === goalId ? { ...goal, [field]: value } : goal
      )
    }));
  };

  const handleRemoveGoal = (goalId: string) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.filter((goal: any) => goal.id !== goalId)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSaveSuccess(false);
    
    try {
      // Validate goals before saving
      const invalidGoals = formData.goals.filter((goal: any) => 
        !goal.name.trim() || !goal.type || !goal.value.trim()
      );
      
      if (invalidGoals.length > 0) {
        throw new Error('Please fill in all required fields for each goal (Name, Type, and Value)');
      }
      
      console.log('💾 Saving project with goals:', {
        projectId: project.id,
        goalsToSave: formData.goals,
        goalCount: formData.goals.length
      });
      
      await onUpdate(formData);
      
      console.log('✅ Project saved successfully');
      setSaveSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error: any) {
      console.error('❌ Failed to update project:', error);
      // The error will be shown by the toast in the parent component
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    
    try {
      await onDelete();
    } catch (error) {
      console.error('Failed to delete project:', error);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center">
        <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-3">
          <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Project Settings</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Manage your project details and preferences</p>
        </div>
      </div>

      {/* Project Details Form */}
      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="name">Project Name</Label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="websiteUrl">Website URL</Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <Input
                id="websiteUrl"
                type="url"
                value={formData.websiteUrl}
                onChange={(e) => handleChange('websiteUrl', e.target.value)}
                className="pl-10"
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="industry">Industry</Label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <select
                id="industry"
                value={formData.industry}
                onChange={(e) => handleChange('industry', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select industry</option>
                <option value="E-commerce">E-commerce</option>
                <option value="Technology">Technology</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Finance">Finance</option>
                <option value="Education">Education</option>
                <option value="Marketing">Marketing</option>
                <option value="Retail">Retail</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
              Project is active
            </label>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isLoading}
              className={`${
                saveSuccess 
                  ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800' 
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </div>
              ) : saveSuccess ? (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved!
                </div>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </Card>

      {/* Goal Configuration */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mr-3">
              <Target className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Conversion Goals
                {formData.goals.length > 0 && (
                  <span className="ml-2 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs">
                    {formData.goals.length} goal{formData.goals.length !== 1 ? 's' : ''} configured
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Set up goals to track important user actions
                {goalOptions && (
                  <span className="ml-2 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs">
                    {goalOptions.dataSource === 'ga4_real' ? 'Live GA4 Data' : 
                     goalOptions.dataSource === 'ga4_fallback' ? 'GA4 Connected' : 'Sample Data'}
                  </span>
                )}
              </p>
            </div>
          </div>
        
          {goalOptions && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={fetchGoalOptions}
              disabled={isLoadingGoalOptions}
              className="border-purple-200 dark:border-purple-600 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingGoalOptions ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
          )}
        </div>

        {/* Goal Info */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3">
              ℹ️
            </div>
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-1">Smart Goal Selection</h4>
              <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                Choose from real pages and events from your website data:
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>• <strong>Page Goals:</strong> Select from your actual website pages with view counts</li>
                <li>• <strong>Event Goals:</strong> Choose from tracked events like purchases, sign-ups, and contacts</li>
                <li>• <strong>Custom Events:</strong> Pick from any custom events you're tracking</li>
                <li>• <strong>Live Data:</strong> {goalOptions?.dataSource === 'ga4_real' ? 'Connected to GA4 - showing real data' : 'Sample data shown when GA4 not connected'}</li>
                <li>• <strong>Save:</strong> Goals are automatically saved when you click "Save Changes" above</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Display Saved Goals */}
          {formData.goals.length > 0 && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-200 dark:border-purple-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-purple-900 dark:text-purple-300 flex items-center">
                  <Target className="w-5 h-5 mr-2" />
                  Configured Goals ({formData.goals.length}/5)
                </h4>
                <Button 
                  type="button"
                  onClick={handleAddGoal}
                  disabled={formData.goals.length >= 5}
                  variant="outline"
                  size="sm"
                  className="border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Goal {formData.goals.length >= 5 ? '(Max 5)' : ''}
                </Button>
              </div>
              
              <div className="grid gap-4">
                {formData.goals.map((goal: any, index: number) => (
                  <div key={goal.id} className="bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 mr-3">
                            Goal {index + 1}
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {goal.type === 'pageview' ? 'Page View' : 
                             goal.type === 'event' ? 'Event' :
                             goal.type === 'purchase' ? 'Purchase' :
                             goal.type === 'signup' ? 'Sign Up' :
                             goal.type === 'contact' ? 'Contact' :
                             goal.type === 'download' ? 'Download' :
                             'Custom'}
                          </span>
                        </div>
                        
                        <h5 className="font-semibold text-gray-900 dark:text-white mb-2">{goal.name || 'Untitled Goal'}</h5>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-600 dark:text-gray-400">Target:</span>
                            <p className="text-gray-900 dark:text-gray-100 mt-1 font-mono bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                              {goal.value || 'Not specified'}
                            </p>
                          </div>
                          {goal.description && (
                            <div>
                              <span className="font-medium text-gray-600 dark:text-gray-400">Description:</span>
                              <p className="text-gray-900 dark:text-gray-100 mt-1">{goal.description}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          type="button"
                          onClick={() => setEditingGoalId(editingGoalId === goal.id ? null : goal.id)}
                          variant="ghost"
                          size="sm"
                          className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                        >
                          {editingGoalId === goal.id ? 'Cancel' : 'Edit'}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => handleRemoveGoal(goal.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Edit Form */}
                    {editingGoalId === goal.id && (
                      <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-700 bg-purple-25 dark:bg-purple-900/10">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor={`edit-goal-name-${goal.id}`}>Goal Name</Label>
                            <Input
                              id={`edit-goal-name-${goal.id}`}
                              value={goal.name}
                              onChange={(e) => handleUpdateGoal(goal.id, 'name', e.target.value)}
                              placeholder="e.g., Purchase, Sign up, Contact"
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor={`edit-goal-type-${goal.id}`}>Goal Type</Label>
                            <select
                              id={`edit-goal-type-${goal.id}`}
                              value={goal.type}
                              onChange={(e) => handleUpdateGoal(goal.id, 'type', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="pageview">Page View</option>
                              <option value="event">Event</option>
                              <option value="purchase">Purchase</option>
                              <option value="signup">Sign Up</option>
                              <option value="contact">Contact Form</option>
                              <option value="download">Download</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>

                          <div>
                            <Label htmlFor={`edit-goal-value-${goal.id}`}>
                              {goal.type === 'pageview' ? 'Select Page' : 'Select Event'}
                            </Label>
                            {goalOptions ? (
                              <select
                                id={`edit-goal-value-${goal.id}`}
                                value={goal.value}
                                onChange={(e) => handleUpdateGoal(goal.id, 'value', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="">
                                  {goal.type === 'pageview' ? 'Select a page...' : 'Select an event...'}
                                </option>
                                {goal.type === 'pageview' && goalOptions.pages?.map((page: any) => (
                                  <option key={page.path} value={page.path}>
                                    {page.path} - {page.title} ({page.pageviews.toLocaleString()} views)
                                  </option>
                                ))}
                                {(goal.type === 'event' || goal.type === 'purchase' || goal.type === 'signup' || goal.type === 'contact' || goal.type === 'download') && 
                                  goalOptions.events?.map((event: any) => (
                                    <option key={event.name} value={event.name}>
                                      {event.name} - {event.description} ({event.count.toLocaleString()} events)
                                    </option>
                                  ))}
                                {goal.type === 'custom' && goalOptions.customEvents?.map((event: any) => (
                                  <option key={event.name} value={event.name}>
                                    {event.name} - {event.description} ({event.count.toLocaleString()} events)
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <Input
                                id={`edit-goal-value-${goal.id}`}
                                value={goal.value}
                                onChange={(e) => handleUpdateGoal(goal.id, 'value', e.target.value)}
                                placeholder={
                                  goal.type === 'pageview' ? '/thank-you' : 
                                  goal.type === 'event' ? 'purchase' :
                                  'goal_value'
                                }
                              />
                            )}
                          </div>

                          <div>
                            <Label htmlFor={`edit-goal-description-${goal.id}`}>Description (Optional)</Label>
                            <Input
                              id={`edit-goal-description-${goal.id}`}
                              value={goal.description}
                              onChange={(e) => handleUpdateGoal(goal.id, 'description', e.target.value)}
                              placeholder="Brief description of this goal"
                            />
                          </div>
                        </div>
                        
                        <div className="flex justify-end mt-4">
                          <Button
                            type="button"
                            onClick={() => setEditingGoalId(null)}
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {formData.goals.length === 0 && (
            <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
              <Target className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-300 mb-2">No goals configured</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Add your first conversion goal to start tracking</p>
              <Button 
                type="button"
                onClick={handleAddGoal}
                disabled={formData.goals.length >= 5}
                variant="outline"
                className="border-purple-200 dark:border-purple-600 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Goal {formData.goals.length >= 5 ? '(Max 5)' : `(${formData.goals.length}/5)`}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="p-6 border-red-200 dark:border-red-700 bg-white dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Danger Zone</h3>
        
        {showDeleteConfirm ? (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-700">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3" />
              <div className="flex-1">
                <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">Are you sure you want to delete this project?</h4>
                <p className="text-sm text-red-700 dark:text-red-300 mb-4">
                  This action cannot be undone. All data associated with this project will be permanently deleted.
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <div className="flex items-center">
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Deleting...
                      </div>
                    ) : (
                      'Yes, Delete Project'
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Delete this project</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Once deleted, all data will be permanently removed.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Project
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}